import { useQuery, useMutation } from '@apollo/client';
import { Box, Typography, Button, Chip, Table, Sheet, CircularProgress, IconButton } from '@mui/joy';
import { People, Delete as DeleteIcon } from '@mui/icons-material';
import { LIST_ATS_IMPORTS } from './queries';
import { useFeedback } from '../feedback/Service';
import { parseGraphQLError } from '../../utils';
import { getProviderQueries } from './providerQueryMap';

const ATSCandidatesList = ({ cavalryJobId, show, onToggle, providerId = 'ashby' }) => {
  const feedback = useFeedback();
  const pq = getProviderQueries(providerId);

  const { data: importsData, loading, error, refetch } = useQuery(LIST_ATS_IMPORTS, {
    variables: { cavalryJobId },
    skip: !cavalryJobId,
    onError: (error) => {
      console.error('Error fetching imported candidates:', error);
    }
  });

  const [deleteImport, { loading: deleting }] = useMutation(pq.DELETE_IMPORTED_CANDIDATE, {
    refetchQueries: [{ query: LIST_ATS_IMPORTS, variables: { cavalryJobId } }],
    onCompleted: () => {
      feedback.snackbar({ text: 'Imported candidate removed successfully!', type: 'success' });
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    }
  });

  const handleDelete = async (importId, candidateName) => {
    const confirmed = await feedback.confirm({
      title: 'Remove Imported Candidate?',
      description: `This will remove "${candidateName}" from the imported candidates list.`,
    });
    if (confirmed) {
      deleteImport({ variables: { importId } });
    }
  };

  const imports = importsData?.listAtsImports || [];

  return (
    <>
      <Button
        variant="outlined"
        color="neutral"
        startDecorator={<People />}
        onClick={() => {
          onToggle();
        }}
      >
        {show ? 'Hide' : 'View'} Imported Candidates ({imports.length})
      </Button>

      {show && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography level="title-sm">Imported Candidates</Typography>
            <Button
              size="sm"
              variant="plain"
              onClick={onToggle}
            >
              Hide
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size="sm" />
              <Typography level="body-sm">Loading imported candidates...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ p: 2, bgcolor: 'warning.softBg', borderRadius: 'sm' }}>
              <Typography level="body-sm" color="warning">
                Failed to load imported candidates. Please try again.
              </Typography>
              <Button size="sm" variant="soft" color="warning" sx={{ mt: 1 }} onClick={() => refetch()}>
                Retry
              </Button>
            </Box>
          ) : imports.length > 0 ? (
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
              <Table size="sm" stickyHeader sx={{ tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th style={{ width: '22%' }}>Name</th>
                    <th style={{ width: '30%' }}>Email</th>
                    <th style={{ width: '12%' }}>Status</th>
                    <th style={{ width: '20%' }}>Imported At</th>
                    <th style={{ width: '16%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.map((imported) => (
                    <tr key={imported._id}>
                      <td>{imported.candidateName}</td>
                      <td style={{ wordBreak: 'break-word' }}>{imported.candidateEmail}</td>
                      <td>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={imported.status === 'synced' ? 'success' : imported.status === 'pending' ? 'warning' : 'neutral'}
                        >
                          {imported.status}
                        </Chip>
                      </td>
                      <td>
                        {new Date(imported.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          disabled={deleting}
                          onClick={() => handleDelete(imported._id, imported.candidateName)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          ) : (
            <Typography level="body-sm" color="neutral">
              No candidates have been imported yet. Click &ldquo;Import Candidates&rdquo; to get started.
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};

export default ATSCandidatesList;
